import React, { useState, useEffect } from 'react'
import { bankQuestionService } from '../../services/bankQuestionService_moi'

const ImagePathTest: React.FC = () => {
  const [questions, setQuestions] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const testImagePaths = async () => {
    setLoading(true)
    setError(null)
    
    try {
      console.log('🔍 Testing image path processing...')
      
      // Load questions with images
      const quizData = await bankQuestionService.getRandomQuestions('11', '1', '01', {
        thoiGianLamBai: 30,
        diemDat: 70
      })
      
      if (quizData && quizData.cauHoi) {
        const questionsWithImages = quizData.cauHoi.filter(q => 
          q.hinh_anh || q.hinh_anh_giai_thich
        )
        
        console.log(`🖼️ Found ${questionsWithImages.length} questions with images`)
        setQuestions(questionsWithImages)
      }
      
    } catch (err) {
      console.error('❌ Error loading questions:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    testImagePaths()
  }, [])

  const createPlaceholderImage = (fileName: string) => {
    // Create a simple SVG placeholder
    const svg = `
      <svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f0f0f0" stroke="#ccc" stroke-width="2"/>
        <text x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="Arial" font-size="14" fill="#666">
          ${fileName}
        </text>
        <text x="50%" y="70%" text-anchor="middle" dy=".3em" font-family="Arial" font-size="12" fill="#999">
          (Placeholder Image)
        </text>
      </svg>
    `
    return `data:image/svg+xml;base64,${btoa(svg)}`
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Image Path Test</h1>
      
      <div className="mb-6">
        <button
          onClick={testImagePaths}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Test Image Paths'}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
        <h2 className="text-lg font-semibold mb-2">Image Path Processing Info</h2>
        <p className="text-sm text-gray-700 mb-2">
          • Windows paths (D:\PMaker\images\...) will be converted to /images/questions/filename.png
        </p>
        <p className="text-sm text-gray-700 mb-2">
          • Relative paths (/images/...) will be kept as-is
        </p>
        <p className="text-sm text-gray-700">
          • HTTP/HTTPS URLs will be kept as-is
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-gray-100 p-4 rounded">
          <h2 className="text-lg font-semibold mb-2">Summary</h2>
          <p>Total questions loaded: <strong>{questions.length}</strong></p>
          <p>Questions with question images: <strong>{questions.filter(q => q.hinh_anh).length}</strong></p>
          <p>Questions with explanation images: <strong>{questions.filter(q => q.hinh_anh_giai_thich).length}</strong></p>
        </div>

        {questions.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Questions with Images</h2>
            
            <div className="space-y-6">
              {questions.map((question, index) => (
                <div key={question.id} className="border border-gray-300 rounded-lg p-4">
                  <div className="mb-4">
                    <h3 className="font-semibold text-lg">
                      Question {index + 1} (ID: {question.id})
                    </h3>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Question Content:</h4>
                    <p className="text-gray-800 text-sm">{question.cau_hoi?.substring(0, 200)}...</p>
                  </div>

                  {/* Question Image */}
                  {question.hinh_anh && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Question Image:</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Path:</strong> {question.hinh_anh}
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Original path attempt */}
                        <div className="border border-gray-200 p-2 rounded">
                          <p className="text-xs text-gray-500 mb-2">Attempting to load from processed path:</p>
                          <img
                            src={question.hinh_anh}
                            alt="Question image"
                            className="max-w-full h-auto"
                            onLoad={() => console.log(`✅ Image loaded: ${question.hinh_anh}`)}
                            onError={(e) => {
                              console.error(`❌ Failed to load image: ${question.hinh_anh}`)
                              const target = e.currentTarget as HTMLImageElement
                              const fileName = question.hinh_anh.split('/').pop() || 'unknown'
                              target.src = createPlaceholderImage(fileName)
                            }}
                          />
                        </div>

                        {/* Placeholder */}
                        <div className="border border-gray-200 p-2 rounded">
                          <p className="text-xs text-gray-500 mb-2">Placeholder (shows expected filename):</p>
                          <img
                            src={createPlaceholderImage(question.hinh_anh.split('/').pop() || 'unknown')}
                            alt="Placeholder"
                            className="max-w-full h-auto"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Explanation Image */}
                  {question.hinh_anh_giai_thich && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Explanation Image:</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Path:</strong> {question.hinh_anh_giai_thich}
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Original path attempt */}
                        <div className="border border-gray-200 p-2 rounded">
                          <p className="text-xs text-gray-500 mb-2">Attempting to load from processed path:</p>
                          <img
                            src={question.hinh_anh_giai_thich}
                            alt="Explanation image"
                            className="max-w-full h-auto"
                            onLoad={() => console.log(`✅ Explanation image loaded: ${question.hinh_anh_giai_thich}`)}
                            onError={(e) => {
                              console.error(`❌ Failed to load explanation image: ${question.hinh_anh_giai_thich}`)
                              const target = e.currentTarget as HTMLImageElement
                              const fileName = question.hinh_anh_giai_thich.split('/').pop() || 'unknown'
                              target.src = createPlaceholderImage(fileName)
                            }}
                          />
                        </div>

                        {/* Placeholder */}
                        <div className="border border-gray-200 p-2 rounded">
                          <p className="text-xs text-gray-500 mb-2">Placeholder (shows expected filename):</p>
                          <img
                            src={createPlaceholderImage(question.hinh_anh_giai_thich.split('/').pop() || 'unknown')}
                            alt="Placeholder"
                            className="max-w-full h-auto"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded">
        <h3 className="font-semibold mb-2">📋 Next Steps:</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm">
          <li>Check the console for image path conversion logs</li>
          <li>Copy actual image files to <code>public/images/questions/</code> directory</li>
          <li>Ensure image filenames match the converted paths</li>
          <li>Test image loading in quiz interface</li>
        </ol>
      </div>
    </div>
  )
}

export default ImagePathTest